import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken, clearToken, getStoredUser, setStoredUser } from "@/lib/api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser());
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(!!getToken());

  const refreshMe = useCallback(async () => {
    if (!getToken()) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setTenant(data.tenant || null);
      setStoredUser(data.user);
    } catch (e) {
      clearToken();
      setUser(null);
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  const login = async (email, password, tenantSlug = null) => {
    const payload = { email, password };
    if (tenantSlug) payload.tenant_slug = tenantSlug;
    const { data } = await api.post("/auth/login", payload);
    // Ambiguous email: multiple accounts share this email — caller must
    // re-submit with a tenant_slug picked by the user.
    if (data.multiple_accounts) {
      return { multipleAccounts: true, accounts: data.accounts };
    }
    setToken(data.access_token);
    setStoredUser(data.user);
    setUser(data.user);
    await refreshMe();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    clearToken();
    setUser(null);
    setTenant(null);
    window.location.href = "/login";
  };

  return (
    <AuthCtx.Provider value={{ user, tenant, loading, login, logout, refreshMe }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
